import { Inject, Injectable } from '@angular/core';
import { ConfigHttpService } from '@sc/data-access-http-services';
import {
  CLIENT_APP_ID,
  FeatureBrandConfiguration,
  ClientAppId,
  ConfigurationBaseService,
  CreationData,
  DocumentType,
  DocumentTypeEnum,
  DossierGlobalOrigins,
  InsuranceSuggestion,
  OffsetDurations,
  OperationType,
  OriginFilter,
  PublicConfiguration,
  country,
} from '@sc/data-access-models';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface ApprovedPartnerConfiguration {
  id: string;
  logo: string;
  name: string;
  brand?: FeatureBrandConfiguration;
}

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService extends ConfigurationBaseService {
  private readonly _config$ = new BehaviorSubject<PublicConfiguration>(null);

  constructor(
    private readonly configHttpService: ConfigHttpService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {
    super();
  }

  init$() {
    return this.configHttpService
      .fetchConfig()
      .pipe(tap((result) => this.setConfig(result)));
  }

  setConfig(config: PublicConfiguration) {
    this._config$.next(config);
    this.configuration = config;
  }

  getConfig(): PublicConfiguration {
    return this._config$.value;
  }

  filterInsuranceSuggestions(
    insuranceSuggestion: InsuranceSuggestion[],
    filter: string
  ): string[] {
    return this.filterSuggestions(
      insuranceSuggestion.map((x) => x.insuranceName),
      filter
    );
  }

  filterSuggestions(suggestionList: string[], filter: string): string[] {
    if (!filter) {
      return suggestionList;
    }

    const lowerFilter = filter.toLowerCase();
    return suggestionList.filter((suggestion) => {
      const lowerSuggestion = suggestion.toLowerCase();
      return lowerSuggestion.includes(lowerFilter);
    });
  }

  getSortedInsuranceSuggestions(): InsuranceSuggestion[] {
    return this.getConfig().insuranceSuggestion.sort((a, b) =>
      a.insuranceName.localeCompare(b.insuranceName)
    );
  }

  isFrenchContext() {
    return 'fr' === this._config$.value.map.defaultCountryCode;
  }

  getAllLicensePlateCountries(): country[] {
    return this._config$.value.patterns.licensePlate.map(
      (pattern) => pattern.value
    );
  }

  getDefaultLicensePlateCountry(): country {
    return this._config$.value.patterns.licensePlate[0]?.value;
  }

  getDefaultPhoneNumberCountry(): country {
    return this.getConfig()?.patterns?.phoneNumber[0];
  }

  getGlassUsage() {
    return this.clientAppId === 'insurer' &&
      !!this.getConfig()?.glassUsageInsurer
      ? this.getConfig()?.glassUsageInsurer
      : this.getConfig()?.glassUsage;
  }

  getAllExcess() {
    const excess0 = this.getConfig().excessOptions.excessList.excess0;
    const excessWithValue =
      this.getConfig().excessOptions.excessList.excessWithValue;
    const proportionalExcess =
      this.getConfig().excessOptions.excessList.proportionalExcess;
    return [...excess0, ...excessWithValue, ...proportionalExcess];
  }

  getFitterBrandInfosByBrandId(brandId: string) {
    return this.getConfig()?.fitterBrands?.find(
      (brand) => brand.id === brandId
    );
  }

  getBrandName(brandId: string) {
    return this.getFitterBrandInfosByBrandId(brandId)?.name ?? '';
  }

  getBrandSlotSearchDateRange(brandId: string) {
    return (
      this.getFitterBrandInfosByBrandId(brandId)?.booking?.search
        ?.rangeByDays ?? 20
    );
  }

  getBrandSearchVersion(brandId: string) {
    return this.getFitterBrandInfosByBrandId(brandId)?.booking?.search?.version;
  }

  getBrandMobilityHalfDayLimit(brandId: string) {
    return (
      this.getFitterBrandInfosByBrandId(brandId)?.mobilityHalfDayLimit ??
      '12:00'
    );
  }

  hasNoSlotFitterWithDelayDisplay(): boolean {
    return !!this.getConfig()?.fitterBrands?.find(
      (brand) =>
        brand?.booking?.search?.version === 'NO_SLOT' &&
        brand?.features?.includes('should-display-later')
    );
  }

  getBrandDefaultOffset(
    brandId: string,
    operationType: OperationType,
    inMobility: boolean
  ) {
    const brandSearchObject =
      this.getFitterBrandInfosByBrandId(brandId)?.booking?.search;

    const usedOffsetInfos = inMobility
      ? brandSearchObject?.mobilityOffset
      : brandSearchObject?.offset;

    if (operationType === 'Replace') {
      return usedOffsetInfos?.replace ?? 20;
    } else {
      return usedOffsetInfos?.repair ?? 1;
    }
  }
  getBrandDefaultOffsets(brandId: string): {
    center: OffsetDurations;
    mobility: OffsetDurations;
  } {
    const brandSearchObject =
      this.getFitterBrandInfosByBrandId(brandId)?.booking?.search;

    return {
      center: brandSearchObject?.offset,
      mobility: brandSearchObject?.mobilityOffset,
    };
  }

  getDefaultTimezone() {
    return this._config$.value.uniqueTimezone || 'Etc/UTC';
  }

  getSupportedDocumentTypes(): DocumentType[] {
    return (
      this._config$.value.supportedDocuments ||
      (Object.values(DocumentTypeEnum) as unknown as DocumentType[])
    );
  }

  getLogoutDelay(userGroups: string[]) {
    let defaultDelay = 5 * 60; // 5 heures par défaut
    if (this._config$.value.autoLogoutDelay?.length > 0) {
      const conf = this._config$.value.autoLogoutDelay.filter(
        (logoutConfig) => !logoutConfig.group
      );
      if (conf.length > 0) defaultDelay = conf[0].delay;
      const otherDelay = this._config$.value.autoLogoutDelay
        .filter((logoutConfig) => logoutConfig.group)
        .map((logoutConfig) => {
          if (userGroups && userGroups.indexOf(logoutConfig.group) > -1) {
            return logoutConfig.delay;
          } else {
            return defaultDelay;
          }
        });
      if (otherDelay.length > 0) return Math.min(...otherDelay);
    }
    return defaultDelay;
  }

  getOriginFiltersAllowed() {
    return this.getConfig()
      .originFilters.map((originFilter) => originFilter.lib)
      .filter((lib, index, self) => self.indexOf(lib) === index);
  }

  getOriginFiltersB2B() {
    return this.getConfig()
      .originFilters.filter((originFilter) => originFilter.origin === 'B2B')
      .map((originFilter) => originFilter.lib)
      .filter((lib, index, self) => self.indexOf(lib) === index);
  }

  getFitterBrands() {
    return this.getConfig().fitterBrands;
  }

  getApprovedPartnerConfigs(): ApprovedPartnerConfiguration[] {
    const approvedPartners = this.getConfig()?.approvedPartners ?? [];
    const fitterBrands = this.getConfig()?.fitterBrands ?? [];

    return approvedPartners
      .map((partnerId) =>
        this.buildApprovedPartnerConfig(partnerId, fitterBrands)
      )
      .filter(
        (partner): partner is ApprovedPartnerConfiguration => partner !== null
      );
  }

  getOriginFormLib(lib: string): string {
    return this.getConfig().originFilters.find((filter) => filter.lib === lib)
      ?.origin;
  }

  private buildApprovedPartnerConfig(
    partnerId: string,
    fitterBrands: FeatureBrandConfiguration[]
  ): ApprovedPartnerConfiguration | null {
    const normalizedPartnerId = this.normalizeApprovedPartnerId(partnerId);

    if (!normalizedPartnerId) {
      return null;
    }

    const brand = fitterBrands.find(({ id }) => id === normalizedPartnerId);

    return {
      id: normalizedPartnerId,
      logo:
        brand?.logo || this.getApprovedPartnerLogoFileName(normalizedPartnerId),
      name: brand?.name || normalizedPartnerId,
      brand,
    };
  }

  private normalizeApprovedPartnerId(
    partnerId: string | null | undefined
  ): string | null {
    return partnerId?.trim() || null;
  }

  private getApprovedPartnerLogoFileName(partnerId: string): string {
    return partnerId.includes('.') ? partnerId : `${partnerId}.png`;
  }

  getDossierGlobalOriginFromCreationData(
    creationData: CreationData,
    clientAppId: ClientAppId
  ): DossierGlobalOrigins {
    if (!creationData) {
      return getDefaultOrigin(clientAppId);
    }
    for (const filter of this.getConfig().originFilters) {
      if (
        (filter.origin === creationData.origin || filter.origin === '*') &&
        (filter.businessOrigin === creationData.businessOrigin?.split('_')[0] ||
          filter.businessOrigin === '*' ||
          filter.businessOrigin === creationData.businessOrigin)
      ) {
        return filter.lib;
      }
    }
    return 'OTHER';
  }

  getOriginFilterFormCreationData(creationData: CreationData): OriginFilter {
    for (const filter of this.getConfig().originFilters) {
      if (
        (filter.origin === creationData?.origin || filter.origin === '*') &&
        (filter.businessOrigin ===
          creationData?.businessOrigin?.split('_')[0] ||
          filter.businessOrigin === '*')
      ) {
        return filter;
      }
    }
    return null;
  }
}

function getDefaultOrigin(clientAppId: ClientAppId): DossierGlobalOrigins {
  switch (clientAppId) {
    case 'customer':
      return 'SITE_WEB';
    case 'fitter':
      return 'AGENDA';
    case 'insurer':
      return 'PARTNER';
    default:
      return 'OTHER';
  }
}
